import { DotLottie } from 'https://cdn.jsdelivr.net/npm/@lottiefiles/dotlottie-web/+esm';

const MINUS_SVG = '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M18 12L6 12" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/></svg>';
const PLUS_SVG = '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 6L12 18M18 12L6 12" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/></svg>';

if (!customElements.get('hormone-journey')) {
  class HormoneJourney extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;
      this._waitForGsap();
    }

    _waitForGsap() {
      if (typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined') {
        this._init();
      } else {
        requestAnimationFrame(() => this._waitForGsap());
      }
    }

    _init() {
      gsap.registerPlugin(ScrollTrigger);

      this.stageItems = [...this.querySelectorAll('[data-stage-item]')];
      this.progressFill = this.querySelector('[data-progress-fill]');
      this.progressTrack = this.querySelector('[data-progress-track]');
      this.scrollGrid = this.querySelector('[data-scroll-grid]');
      this.activeIndex = 0;
      this.totalFrames = 0;
      this.frameRate = 30;
      this._rafId = null;
      this._currentFrame = 0;
      this._lottieInstances = [];
      this._scrollMode = false;
      this._clickMode = false;

      this.stageCount = this.stageItems.length;
      this.isDesktop = window.innerWidth >= 1024;

      this._stageContents = this.stageItems.map(s => s.querySelector('[data-stage-content]'));
      this._stageIcons = this.stageItems.map(s => s.querySelector('[data-stage-icon]'));
      this._stageToggles = this.stageItems.map(s => s.querySelector('[data-stage-toggle]'));
      this._cacheHeights();

      this.stageItems.forEach((item, index) => {
        const toggle = item.querySelector('[data-stage-toggle]');
        toggle.addEventListener('click', () => this.handleClick(index));
      });

      this.openStage(0);
      this.progressFill.style.height = '0%';

      this.initLottie();

      if (this.isDesktop) {
        this._initScrollDriven();
      } else {
        this._initClickDriven();
      }

      this._onResize = this._handleResize.bind(this);
      window.addEventListener('resize', this._onResize);
    }

    _cacheHeights() {
      const prev = this._stageContents.map(c => c.style.maxHeight);
      this._stageContents.forEach(c => { c.style.maxHeight = 'none'; });
      this._contentHeights = this._stageContents.map(c => c.scrollHeight);
      this._stageContents.forEach((c, i) => { c.style.maxHeight = prev[i]; });
    }

    _initClickDriven() {
      this._clickMode = true;
      this._benefitTimeouts = [];

      const startPercent = this._getDotPercent(0);
      const endPercent = this._getContentEndPercent(0);

      this.progressFill.style.top = startPercent + '%';
      this.progressFill.style.height = '0%';

      requestAnimationFrame(() => {
        this.progressFill.style.transition = 'height 500ms ease-out';
        this.progressFill.style.height = (endPercent - startPercent) + '%';
      });

      this._staggerBenefits(0);
    }

    _initScrollDriven() {
      this._scrollMode = true;

      this.progressFill.style.transition = 'none';

      const vpH = window.innerHeight;
      this._columnsGrid = this.scrollGrid.querySelector('.grid');
      const gridH = this._columnsGrid.offsetHeight;

      const scrollDistance = vpH * 0.6 * this.stageCount;
      this.scrollGrid.style.minHeight = (scrollDistance + vpH) + 'px';

      this._columnsGrid.style.position = 'sticky';
      this._columnsGrid.style.top = Math.max(0, (vpH - gridH) / 2) + 'px';

      this._scrollTrigger = ScrollTrigger.create({
        trigger: this.scrollGrid,
        start: 'top top',
        end: 'bottom bottom',
        onUpdate: (self) => this._onScrollProgress(self.progress),
      });
    }

    _onScrollProgress(progress) {
      const stageCount = this.stageCount;
      const stageProgress = progress * stageCount;
      const stageIndex = Math.min(Math.floor(stageProgress), stageCount - 1);
      const stageLocal = stageProgress - stageIndex;

      if (stageIndex !== this.activeIndex) {
        this.activeIndex = stageIndex;
        this.openStage(stageIndex);
        this.playLottieSegment(stageIndex);
      }

      const startPercent = this._getDotPercent(stageIndex);
      const endPercent = this._getContentEndPercent(stageIndex);

      this.progressFill.style.top = startPercent + '%';
      this.progressFill.style.height = (stageLocal * (endPercent - startPercent)) + '%';

      this._updateBenefitOpacity();
    }

    _handleResize() {
      const wasDesktop = this.isDesktop;
      this.isDesktop = window.innerWidth >= 1024;

      this._sizeLottieCanvases();

      this._cacheHeights();

      if (wasDesktop !== this.isDesktop) {
        this._teardown();
        this.activeIndex = 0;
        this.openStage(0);
        this.progressFill.style.height = '0%';

        if (this.isDesktop) {
          this._initScrollDriven();
        } else {
          this._initClickDriven();
        }
      }
    }

    _teardown() {
      if (this._scrollTrigger) {
        this._scrollTrigger.kill();
        this._scrollTrigger = null;
      }
      if (this._columnsGrid) {
        this._columnsGrid.style.position = '';
        this._columnsGrid.style.top = '';
      }
      if (this.scrollGrid) {
        this.scrollGrid.style.minHeight = '';
      }
      if (this._benefitTimeouts) {
        this._benefitTimeouts.forEach(t => clearTimeout(t));
        this._benefitTimeouts = [];
      }
      this.progressFill.style.transition = '';
      this.progressFill.style.top = '';
      this._scrollMode = false;
      this._clickMode = false;
    }

    disconnectedCallback() {
      this._teardown();
      this._stopNoise();
      window.removeEventListener('resize', this._onResize);
      if (this._resizeObserver) this._resizeObserver.disconnect();
      if (this._visObserver) this._visObserver.disconnect();
      if (this._rafId) cancelAnimationFrame(this._rafId);
    }

    initLottie() {
      this._lottieStack = this.querySelector('[data-lottie-stack]');
      if (!this._lottieStack) return;

      this._lottieCanvases = [...this._lottieStack.querySelectorAll('canvas[data-lottie-src]')];
      this._noiseOverlay = this._lottieStack.querySelector('[data-noise-overlay]');
      this._pillCanvas = this._lottieStack.querySelector('[data-lottie-layer="pill"]');
      this._noiseCtx = null;
      this._noiseSource = null;
      this._noiseRaf = null;
      this._maxDpr = Math.min(window.devicePixelRatio || 1, 1.5);

      this._sizeLottieCanvases();

      let i = 0;
      const loadNext = () => {
        if (i >= this._lottieCanvases.length) {
          this._onLottiesLoaded();
          return;
        }
        const canvas = this._lottieCanvases[i++];
        const src = canvas.dataset.lottieSrc;
        if (!src) { loadNext(); return; }
        const instance = new DotLottie({ canvas, src, autoplay: false, loop: false });
        this._lottieInstances.push(instance);
        instance.addEventListener('load', () => loadNext());
      };
      loadNext();
    }

    _onLottiesLoaded() {
      const primary = this._lottieInstances[0];
      if (primary) {
        this.totalFrames = primary.totalFrames;
        const duration = primary.duration;
        this.frameRate = duration > 0 ? this.totalFrames / duration : 30;

        const animSize = primary.animationSize();
        if (animSize.width > 0 && animSize.height > 0) {
          this._lottieStack.style.aspectRatio = `${animSize.width} / ${animSize.height}`;
        }
      }

      if (this._noiseOverlay) {
        this._noiseCtx = this._noiseOverlay.getContext('2d');
      }

      this._sizeLottieCanvases();
      this.playLottieSegment(0);

      this._visObserver = new IntersectionObserver(([e]) => {
        if (e.isIntersecting) this._startNoiseLoop();
        else this._stopNoise();
      }, { threshold: 0.05 });
      this._visObserver.observe(this._lottieStack);

      this._resizeObserver = new ResizeObserver(() => this._sizeLottieCanvases());
      this._resizeObserver.observe(this._lottieStack);
    }

    _sizeLottieCanvases() {
      if (!this._lottieStack) return;
      const w = this._lottieStack.clientWidth;
      const h = this._lottieStack.clientHeight;
      if (w === 0 || h === 0) return;

      this._lottieCanvases.forEach(c => {
        const layer = c.dataset.lottieLayer;
        const dpr = (layer === 'background' || layer === 'pill') ? 0.5 : this._maxDpr;
        c.width = Math.round(w * dpr);
        c.height = Math.round(h * dpr);
      });

      if (this._noiseOverlay) {
        const px = Math.round(w * this._maxDpr);
        this._noiseOverlay.width = px;
        this._noiseOverlay.height = px;
        this._noiseSource = this._generateNoise(px, px);
      }

      this._lottieInstances.forEach(inst => inst.resize());
    }

    handleClick(index) {
      if (this._scrollMode) {
        this._scrollToStage(index);
        return;
      }

      if (this._benefitTimeouts) {
        this._benefitTimeouts.forEach(t => clearTimeout(t));
        this._benefitTimeouts = [];
      }

      this.activeIndex = index;
      this.playLottieSegment(index);
      this.openStage(index);

      const startPercent = this._getDotPercent(index);
      const endPercent = this._getContentEndPercent(index);

      this.progressFill.style.transition = 'none';
      this.progressFill.style.top = startPercent + '%';
      this.progressFill.style.height = '0%';

      requestAnimationFrame(() => {
        this.progressFill.style.transition = 'height 500ms ease-out';
        this.progressFill.style.height = (endPercent - startPercent) + '%';
      });

      this._staggerBenefits(index);
    }

    _scrollToStage(index) {
      if (!this._scrollTrigger) return;
      const targetProgress = (index + 0.5) / this.stageCount;
      const start = this._scrollTrigger.start;
      const end = this._scrollTrigger.end;
      const targetScroll = start + targetProgress * (end - start);
      window.scrollTo({ top: targetScroll, behavior: 'smooth' });
    }

    openStage(index) {
      this.stageItems.forEach(s => {
        if (s._flipAnim) { s._flipAnim.cancel(); s._flipAnim = null; }
        s.querySelectorAll('[data-benefit-item]').forEach(b => {
          b.style.opacity = '';
        });
      });
      this._stageContents.forEach(c => {
        if (c._clipAnim) { c._clipAnim.cancel(); c._clipAnim = null; }
      });

      const firstRects = this.stageItems.map(s => s.getBoundingClientRect());

      this.stageItems.forEach((stage, i) => {
        const content = this._stageContents[i];
        const isActive = i === index;

        content.style.maxHeight = isActive ? this._contentHeights[i] + 'px' : '0px';

        if (isActive) {
          stage.classList.add('is-active');
          this._stageIcons[i].innerHTML = MINUS_SVG;
          this._stageToggles[i].setAttribute('aria-expanded', 'true');
        } else {
          stage.classList.remove('is-active');
          this._stageIcons[i].innerHTML = PLUS_SVG;
          this._stageToggles[i].setAttribute('aria-expanded', 'false');
        }
      });

      const lastRects = this.stageItems.map(s => s.getBoundingClientRect());
      const dur = 500;

      this.stageItems.forEach((stage, i) => {
        const dy = firstRects[i].top - lastRects[i].top;
        if (Math.abs(dy) < 0.5) return;

        stage._flipAnim = stage.animate(
          [{ transform: `translateY(${dy}px)` }, { transform: 'translateY(0)' }],
          { duration: dur, easing: 'ease-out' }
        );
        stage._flipAnim.onfinish = () => { stage._flipAnim = null; };
      });

      const openContent = this._stageContents[index];
      openContent._clipAnim = openContent.animate(
        [{ clipPath: 'inset(0 0 100% 0)' }, { clipPath: 'inset(0 0 0% 0)' }],
        { duration: dur, easing: 'ease-out' }
      );
      openContent._clipAnim.onfinish = () => { openContent._clipAnim = null; };

      this._updateTrackHeight();
    }

    _updateTrackHeight() {
      if (!this.progressTrack) return;
      const fillContainer = this.progressFill.parentElement;
      const containerOffset = fillContainer.offsetTop;
      const containerH = fillContainer.offsetHeight;
      if (containerH === 0) return;

      const lastStage = this.stageItems[this.stageCount - 1];
      const lastStageBottom = lastStage.offsetTop + lastStage.offsetHeight - containerOffset + 36;

      this.progressTrack.style.height = lastStageBottom + 'px';
    }

    _getDotPercent(index) {
      const stageItem = this.stageItems[index];
      const dot = stageItem.querySelector('[data-stage-dot]');
      const fillContainer = this.progressFill.parentElement;
      const fillContainerH = fillContainer.offsetHeight;
      if (fillContainerH === 0) return 0;

      const dotTopInParent = stageItem.offsetTop + dot.offsetTop;
      const dotCenter = dotTopInParent + dot.offsetHeight / 2;
      return (dotCenter - fillContainer.offsetTop) / fillContainerH * 100;
    }

    _getContentEndPercent(index) {
      const stageItem = this.stageItems[index];
      const content = this._stageContents[index];
      const fillContainer = this.progressFill.parentElement;
      const fillContainerH = fillContainer.offsetHeight;
      if (fillContainerH === 0) return 0;

      const contentBottom = stageItem.offsetTop + content.offsetTop + content.offsetHeight;
      return Math.min((contentBottom - fillContainer.offsetTop) / fillContainerH * 100, 100);
    }

    _updateBenefitOpacity() {
      const fillBottom = this.progressFill.getBoundingClientRect().bottom;
      const activeStage = this.stageItems[this.activeIndex];
      if (!activeStage) return;

      activeStage.querySelectorAll('[data-benefit-item]').forEach(b => {
        if (fillBottom >= b.getBoundingClientRect().top) {
          b.style.opacity = '1';
        } else {
          b.style.opacity = '';
        }
      });
    }

    _staggerBenefits(index) {
      const benefits = this.stageItems[index].querySelectorAll('[data-benefit-item]');
      benefits.forEach((b, i) => {
        this._benefitTimeouts.push(
          setTimeout(() => { b.style.opacity = '1'; }, 200 + i * 150)
        );
      });
    }

    playLottieSegment(index) {
      if (!this._lottieInstances.length || !this.totalFrames) return;

      if (this._rafId) cancelAnimationFrame(this._rafId);

      const framesPerStage = this.totalFrames / this.stageItems.length;
      const toFrame = Math.floor((index + 1) * framesPerStage) - 1;
      const fromFrame = this._currentFrame;
      const direction = fromFrame <= toFrame ? 1 : -1;
      let frame = fromFrame;

      const stagesCrossed = Math.abs(toFrame - fromFrame) / framesPerStage;
      const playbackRate = this.frameRate * Math.max(1, stagesCrossed);

      let lastTime = performance.now();

      const step = (now) => {
        const elapsed = now - lastTime;
        const advance = Math.floor(elapsed * playbackRate / 1000);

        if (advance > 0) {
          frame += direction * advance;
          lastTime = now;

          if (direction > 0 ? frame >= toFrame : frame <= toFrame) {
            frame = toFrame;
            this._lottieInstances.forEach(inst => inst.setFrame(toFrame));
            this._currentFrame = toFrame;
            this._rafId = null;
            return;
          }

          this._lottieInstances.forEach(inst => inst.setFrame(frame));
          this._currentFrame = frame;
        }

        this._rafId = requestAnimationFrame(step);
      };

      this._rafId = requestAnimationFrame(step);
    }

    _generateNoise(w, h) {
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      const imageData = ctx.createImageData(w, h);
      const d = imageData.data;
      for (let i = 0; i < d.length; i += 4) {
        const v = (Math.random() * 255) | 0;
        d[i] = v; d[i + 1] = v; d[i + 2] = v; d[i + 3] = 255;
      }
      ctx.putImageData(imageData, 0, 0);
      return canvas;
    }

    _startNoiseLoop() {
      if (this._noiseRaf || !this._noiseCtx) return;
      let frame = 0;
      const draw = () => {
        this._noiseRaf = requestAnimationFrame(draw);
        if (++frame % 4 !== 0) return;

        const w = this._noiseOverlay.width;
        const h = this._noiseOverlay.height;
        if (!w || !h) return;

        const ctx = this._noiseCtx;
        ctx.clearRect(0, 0, w, h);
        ctx.globalCompositeOperation = 'source-over';
        ctx.globalAlpha = 1;
        if (this._pillCanvas) ctx.drawImage(this._pillCanvas, 0, 0, w, h);
        ctx.globalCompositeOperation = 'source-in';
        if (this._noiseSource) ctx.drawImage(this._noiseSource, 0, 0);
        ctx.globalCompositeOperation = 'source-over';
      };
      this._noiseRaf = requestAnimationFrame(draw);
    }

    _stopNoise() {
      if (this._noiseRaf) {
        cancelAnimationFrame(this._noiseRaf);
        this._noiseRaf = null;
      }
    }

  }

  customElements.define('hormone-journey', HormoneJourney);
}
